import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, CircularProgress, Divider } from '@mui/material';
import { Formik, Form } from 'formik';
import axios from 'axios';
import { READ_RECEIPT_VOUCHER_EXPORT } from '../../../services/apiRoutes';
import CustomCardView from "../../common/CustomCardView";
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import DetailsParticulars from "../../common/DetailsPage/DetailsParticulars";
import { useCompanyDateFormat } from "../../../constants/useCompanyDateFormat";
import { t } from "../../../constants/translationHelper";



const ReceiptVoucherView = ({ handleClose, setMessage, fetchData, receiptVoucherId, currencyList, paymentType, customerName, customerList }) => {
    const companyDateFormat = useCompanyDateFormat();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dataParticular, setDataParticular] = useState(null);
    const [amountData, setAmount] = useState(0);
    const [baseAmountData, setBaseAmount] = useState(0);
    const [currencySymbol, setCurrencySymbol] = useState('');
    const [baseCurrencySymbol, setBaseCurrencySymbol] = useState('');



    useEffect(() => {
        if (!receiptVoucherId) return;

        const fetchData = async () => {
            try {
                const accessToken = sessionStorage.getItem('accessToken');
                const response = await axios.get(READ_RECEIPT_VOUCHER_EXPORT(receiptVoucherId), {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
                const fetchedData = response.data?.receiptVoucher;
                const voucherData = response.data?.receiptVoucher;
                const particulars = fetchedData?.particulars?.map((particular, index) => ({
                    referenceAmount: parseFloat(particular.referenceAmount || 0).toFixed(2),
                    commercialInvoiceNo: particular.exportCiNoList?.commercialInvoiceNo || '',
                    proformaInvoiceNo: particular.exportPiNoList?.proformaInvoiceNo || '',
                    receiptType: particular?.receiptType?.receiptTypeName || '',
                    referenceAmountFooter: parseFloat(particular?.referenceAmount || 0).toFixed(2),
                    amountInBaseCurrencyFooter: parseFloat(particular?.amountInBaseCurrency || 0).toFixed(2),
                    referenceAmount: `${particular?.currency?.currency_symbol || ''} ${parseFloat(particular?.referenceAmount || 0).toFixed(2)}`,
                    amountInBaseCurrency: `${particular?.baseCurrency?.currency_symbol || ''} ${parseFloat(particular?.amountInBaseCurrency || 0).toFixed(2)}`,
                    conversionRate: `${particular?.baseCurrency?.currency_symbol || ''} ${parseFloat(particular?.conversionRate || 0).toFixed(2)}`,
                }));
                const totalReferenceAmount = particulars.reduce((sum, item) => sum + parseFloat(item.referenceAmountFooter), 0);
                const totalAmountInBaseCurrency = particulars.reduce((sum, item) => sum + parseFloat(item.amountInBaseCurrencyFooter || 0), 0);

                setAmount(totalReferenceAmount.toFixed(2));
                setBaseAmount(totalAmountInBaseCurrency.toFixed(2));

                setData(response.data.receiptVoucher);
                setDataParticular(particulars);
                setCurrencySymbol(voucherData?.currency?.currency_symbol || '');
                setBaseCurrencySymbol(voucherData?.baseCurrency?.currency_symbol || '');

            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [receiptVoucherId]);

    if (loading) return <CircularProgress />;
    if (error) return <p>Error: {error}</p>;

    const values = {
        receiptVoucherNo: data?.receiptVoucherNo || '',
        date: companyDateFormat(data?.date) || '',
        customerName: data?.customerName || '',
        companyId: data?.companyId || '',
        receiptTypeId: data?.receiptType?.receiptTypeName || '',
        currencyId: data?.currency?.currency || '',
        baseCurrencyId: data?.baseCurrency?.currency || '',
        note: data?.note || '',
        conversionRate: data?.conversionRate,
        baseAmount: `${data?.baseCurrency?.currency_symbol || ''} ${data?.baseAmount}`,
        bankCharges: `${data?.baseCurrency?.currency_symbol || ''} ${data?.bankCharges}`,
        amount: `${data?.currency?.currency_symbol || ''} ${data?.amount}`,
    };

    const labels = [
        { key: 'receiptVoucherNo', label: 'Receipt Voucher No', sm: 12, md: 8, },
        { key: 'date', label: 'Date', sm: 12, md: 4, },
        { key: 'receiptTypeId', label: 'Receipt Type', sm: 12, md: 4, },
        { key: 'currencyId', label: 'Currency', sm: 12, md: 4, },
        { key: 'amount', label: 'Amount', sm: 12, md: 4, },
        { key: 'baseCurrencyId', label: 'Base Currency', sm: 12, md: 4, },
        { key: 'conversionRate', label: 'Conversion Rate', sm: 12, md: 4, },
        { key: 'baseAmount', label: 'Base Amount', sm: 12, md: 4, },
        { key: 'bankCharges', label: 'Bank Charges', sm: 12, md: 4, },
        { key: 'note', label: 'Note', sm: 12, md: 12, },];


    const particularsColumns = [
        {
            id: 'proformaInvoiceNo',
            label: 'PI No',
            accessor: 'proformaInvoiceNo',
        },
        {
            id: 'commercialInvoiceNo',
            label: 'CI No',
            accessor: 'commercialInvoiceNo',
        },
        {
            id: 'receiptType',
            label: 'receipt Type',
            accessor: 'receiptType',
        },
        {
            id: 'referenceAmount',
            label: 'Amount',
            accessor: 'referenceAmount',
            styleAccessor: {
                textAlign: 'center',
            },
        },
        {
            id: 'conversionRate',
            label: 'conversion Rate',
            accessor: 'conversionRate',
            styleAccessor: {
                textAlign: 'center',
            },
        },
        {
            id: 'amountInBaseCurrency',
            label: 'amount In Base',
            accessor: 'amountInBaseCurrency',
            styleAccessor: {
                textAlign: 'center',
            },
        },
    ];

    const footerData = [
        {
            label: t('Total'),
            footerValue: '',
            style: {
                textAlign: 'right',
            },
            colSpan: 1,
        },
        {
            label: '',
            footerValue: `${currencySymbol} ${amountData}`,
            style: {
                textAlign: 'right',

            },
            colSpan: 3,
        },
        {
            label: '',
            footerValue: `${baseCurrencySymbol} ${baseAmountData}`,
            style: { textAlign: 'right' },
            colSpan: 2,
        },
    ];


    return (
        <Box sx={{ mx: 'auto', mt: 0 }}>
            <Formik initialValues={values} enableReinitialize>
                {() => (
                    <Form>
                        <Grid container spacing={1}>
                            <Grid item xs={4}>
                                <Typography sx={{ fontWeight: 600, fontSize: "18px", color: "#212529", pt: "5px" }}>
                                    {t("Receipt Voucher")}
                                </Typography>
                            </Grid>
                            <Grid item xs={8} style={{ display: "flex", justifyContent: "flex-end" }}>
                                <Box>
                                    <IconButton aria-label="delete">
                                        <DeleteOutlineOutlinedIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                    </IconButton>
                                    <IconButton aria-label="print">
                                        <LocalPrintshopOutlinedIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                    </IconButton>
                                    <IconButton aria-label="close" onClick={handleClose} >
                                        <CloseIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                    </IconButton>
                                </Box>
                            </Grid>
                        </Grid>
                        <Divider />
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <CustomCardView label={labels} data={values} />
                            </Grid>
                            <Grid item xs={12}>
                                <Box display="flex" flexDirection="row" alignItems="center">
                                    <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px", marginLeft: "4px" }}>
                                        {t("Particulars")}
                                    </Typography>
                                </Box>
                                <DetailsParticulars
                                    title={'Particulars'}
                                    columns={particularsColumns}
                                    data={dataParticular}
                                    footerData={footerData}
                                />
                            </Grid>

                        </Grid>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};

export default ReceiptVoucherView;
